import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import apiClient from '../../utils/api';
import { toast } from 'sonner';
import { CheckCircle, User, AlertCircle, Package, Pencil, Trash2 } from 'lucide-react';
import { Checkbox } from '../ui/checkbox';

const StageCompletionModal = ({ isOpen, onClose, order, stageName, stageData, onSuccess, initialProgressDate }) => {
  const [operators, setOperators] = useState([]);
  const [operatorQuantities, setOperatorQuantities] = useState({});
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchingOperators, setFetchingOperators] = useState(true);
  const [completionDate, setCompletionDate] = useState(() => initialProgressDate || new Date().toISOString().slice(0, 10));

  // Per-completion edit
  const [editingIdx, setEditingIdx] = useState(null);
  const [editQty, setEditQty] = useState('');
  const [editLoading, setEditLoading] = useState(false);

  const saveCompletionEdit = async (idx) => {
    const qty = parseInt(editQty);
    if (isNaN(qty) || qty < 0) { toast.error('Enter a valid quantity (0 to delete)'); return; }
    setEditLoading(true);
    try {
      await apiClient.put(`/stages/${stageData.id}/completions/${idx}`, { quantity: qty });
      toast.success(qty === 0 ? 'Completion removed' : `Updated to ${qty} nos`);
      setEditingIdx(null);
      setEditQty('');
      onSuccess && onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update completion');
    } finally {
      setEditLoading(false);
    }
  };

  const deleteCompletion = async (idx, name) => {
    if (!window.confirm(`Remove completion entry${name ? ` by ${name}` : ''}?`)) return;
    setEditLoading(true);
    try {
      await apiClient.put(`/stages/${stageData.id}/completions/${idx}`, { quantity: 0 });
      toast.success('Completion removed');
      onSuccess && onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove completion');
    } finally {
      setEditLoading(false);
    }
  };

  // Stock availability for BS Procurement
  const [stockData, setStockData] = useState([]);
  const [stockSearch, setStockSearch] = useState({ circumference: '', length: '', circumferenceRange: '', lengthRange: '' });
  const [loadingStock, setLoadingStock] = useState(false);

  // Stock selection
  const [selectedStockKey, setSelectedStockKey] = useState(null);
  // Team selection
  const [selectedOperators, setSelectedOperators] = useState([]);
  const [teamQuantity, setTeamQuantity] = useState('');

  const totalRequired = order?.specifications?.nos_of_cylinders || 0;
  // Exclude "quick" placeholders so quick-toggled stages display as 0/N until a real operator is attributed
  const quickTotal = (stageData?.completions || []).filter(c => c.operator_id === 'quick').reduce((s, c) => s + (c.quantity || 0), 0);
  const currentCompleted = Math.max((stageData?.total_completed || 0) - quickTotal, 0);
  const remaining = totalRequired - currentCompleted;

  const isBsProcurement = stageName === 'Base Shell Procurement';
  const isNoOperatorStage = stageName === 'Base Shell Procurement' || stageName === 'Order Received';

  const fetchOperatorsForStage = useCallback(async () => {
    setFetchingOperators(true);
    try {
      const response = await apiClient.get(`/employee-details`);
      const assignedOperators = response.data.filter(emp =>
        emp.processes_performed && emp.processes_performed.includes(stageName)
      );
      setOperators(assignedOperators);
      const initialQuantities = {};
      assignedOperators.forEach(op => { initialQuantities[op.id] = ''; });
      setOperatorQuantities(initialQuantities);
    } catch (error) {
      console.error('Failed to fetch operators:', error);
      setOperators([]);
    } finally {
      setFetchingOperators(false);
    }
  }, [stageName]);

  const fetchStockData = useCallback(async () => {
    setLoadingStock(true);
    try {
      const response = await apiClient.get('/stock');
      setStockData(response.data);
    } catch (error) {
      console.error('Failed to fetch stock data:', error);
      setStockData([]);
    }
    finally { setLoadingStock(false); }
  }, []);

  useEffect(() => {
    if (isOpen && stageName) {
      // Reset completion date to the supplied progress date (or today) each time modal opens
      setCompletionDate(initialProgressDate || new Date().toISOString().slice(0, 10));
      fetchOperatorsForStage();
      if (isBsProcurement) {
        fetchStockData();
        // Pre-fill search with order's specs
        setStockSearch({
          circumference: String(order?.specifications?.circumference || ''),
          length: String(order?.specifications?.length || ''),
          circumferenceRange: '0',
          lengthRange: '0',
        });
      }
    }
  }, [isOpen, stageName, initialProgressDate, isBsProcurement, order, fetchOperatorsForStage, fetchStockData]);

  const getFilteredStock = () => {
    const circ = parseFloat(stockSearch.circumference);
    const len = parseFloat(stockSearch.length);
    const circRange = parseFloat(stockSearch.circumferenceRange) || 0;
    const lenRange = parseFloat(stockSearch.lengthRange) || 0;

    return stockData.filter(row => {
      if (!isNaN(circ) && (row.circumference < circ - circRange || row.circumference > circ + circRange)) return false;
      if (!isNaN(len) && (row.length < len - lenRange || row.length > len + lenRange)) return false;
      return true;
    });
  };

  // Check if exact match exists for this order's specs
  const getExactMatch = () => {
    const circ = order?.specifications?.circumference;
    const len = order?.specifications?.length;
    const bore = order?.specifications?.bore;
    const degree = order?.specifications?.degree;

    return stockData.filter(row => {
      if (row.circumference !== circ || row.length !== len) return false;
      if (row.available <= 0) return false;
      // Check details for bore/degree match
      const hasMatch = row.details?.some(d =>
        (!bore || d.bore === bore) && (!degree || d.degree === degree)
      );
      return hasMatch;
    });
  };

  const handleOperatorToggle = (operatorId, checked) => {
    if (checked) setSelectedOperators(prev => [...prev, operatorId]);
    else setSelectedOperators(prev => prev.filter(id => id !== operatorId));
  };

  const handleSubmit = async () => {
    if (selectedOperators.length === 0) { toast.error('Please select at least one operator'); return; }
    const qty = parseInt(teamQuantity) || 0;
    if (qty === 0) { toast.error('Please enter a quantity'); return; }
    if (qty > remaining) { toast.error(`Quantity (${qty}) exceeds remaining (${remaining})`); return; }

    // Join all selected operator names — single completion entry for the team
    const operatorNames = selectedOperators.map(id => {
      const op = operators.find(o => o.id === id);
      return op?.name || '';
    }).filter(Boolean).join(', ');

    setLoading(true);
    try {
      await apiClient.put(`/stages/${stageData.id}`, {
        operator_id: selectedOperators[0],
        operator_name: operatorNames,
        quantity_completed: qty,
        notes: notes || null,
        completion_date: completionDate ? `${completionDate}T12:00:00Z` : null,
      });
      toast.success(`Added ${qty} completion(s) for team: ${operatorNames}`);
      onSuccess();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update stage'); }
    finally { setLoading(false); }
  };

  const handleClose = () => {
    setOperatorQuantities({});
    setSelectedOperators([]);
    setTeamQuantity('');
    setNotes('');
    onClose();
  };

  const handleSubmitNoOperator = async () => {
    const qty = parseInt(teamQuantity) || 0;
    if (qty === 0) { toast.error('Please enter a quantity'); return; }
    if (qty > remaining) { toast.error(`Quantity (${qty}) exceeds remaining (${remaining})`); return; }
    setLoading(true);
    try {
      await apiClient.put(`/stages/${stageData.id}`, {
        operator_id: 'system',
        operator_name: stageName,
        quantity_completed: qty,
        notes: notes || null,
        completion_date: completionDate ? `${completionDate}T12:00:00Z` : null,
      });
      toast.success(`Marked ${qty} as complete`);
      onSuccess();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update stage'); }
    finally { setLoading(false); }
  };

  const isCompleted = currentCompleted >= totalRequired && totalRequired > 0;
  const filteredStock = isBsProcurement ? getFilteredStock() : [];
  const exactMatches = isBsProcurement ? getExactMatch() : [];

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent data-testid="stage-completion-modal" className={`${isBsProcurement ? 'max-w-3xl' : 'max-w-lg'} max-h-[90vh] overflow-y-auto`}>
        <DialogHeader>
          <DialogTitle className="text-xl font-primary font-semibold flex items-center gap-2">
            {stageName}
            {isCompleted && <CheckCircle className="h-5 w-5 text-emerald-600" />}
          </DialogTitle>
          <div className="text-sm text-slate-600 mt-2 space-y-1">
            <p>Order: <span className="font-medium text-slate-900">{order?.job_name || '-'}</span></p>
            <p>Progress: <span className="font-bold text-slate-900">{currentCompleted}/{totalRequired}</span> cylinders</p>
            {remaining > 0 && (
              <p className="text-blue-600">Remaining: <span className="font-bold">{remaining}</span></p>
            )}
            <div className="grid grid-cols-5 gap-2 mt-2 p-2 bg-slate-50 rounded-lg text-xs">
              <div><span className="text-slate-500">Circ:</span> <span className="font-semibold text-slate-800">{order?.specifications?.circumference || '-'}</span></div>
              <div><span className="text-slate-500">Length:</span> <span className="font-semibold text-slate-800">{order?.specifications?.length || '-'}</span></div>
              <div><span className="text-slate-500">Bore:</span> <span className="font-semibold text-slate-800">{order?.specifications?.bore || '-'}</span></div>
              <div><span className="text-slate-500">Degree:</span> <span className="font-semibold text-slate-800">{order?.specifications?.degree || '-'}</span></div>
              <div><span className="text-slate-500">Key Way:</span> <span className="font-semibold text-slate-800">{order?.specifications?.key_way || '-'}</span></div>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* BS Stock Availability Section - Only for Base Shell Procurement */}
          {isBsProcurement && (
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="bg-slate-100 px-4 py-2 flex items-center gap-2">
                <Package className="h-4 w-4 text-slate-600" />
                <span className="font-semibold text-sm text-slate-800">Stock Availability</span>
                {exactMatches.length > 0 ? (
                  <span className="ml-auto text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded">
                    {exactMatches.reduce((s, r) => s + r.available, 0)} available (exact match)
                  </span>
                ) : (
                  <span className="ml-auto text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded">No exact match in stock</span>
                )}
              </div>
              <div className="p-3 space-y-2">
                {/* Search with ± range */}
                <div className="grid grid-cols-4 gap-2">
                  <div>
                    <Label className="text-[10px] text-slate-500">Circumference</Label>
                    <Input type="number" value={stockSearch.circumference} onChange={(e) => setStockSearch(p => ({ ...p, circumference: e.target.value }))} className="h-7 text-xs" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-500">&plusmn; Range</Label>
                    <Input type="number" value={stockSearch.circumferenceRange} onChange={(e) => setStockSearch(p => ({ ...p, circumferenceRange: e.target.value }))} className="h-7 text-xs" placeholder="0" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-500">Length</Label>
                    <Input type="number" value={stockSearch.length} onChange={(e) => setStockSearch(p => ({ ...p, length: e.target.value }))} className="h-7 text-xs" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-500">&plusmn; Range</Label>
                    <Input type="number" value={stockSearch.lengthRange} onChange={(e) => setStockSearch(p => ({ ...p, lengthRange: e.target.value }))} className="h-7 text-xs" placeholder="0" />
                  </div>
                </div>

                {loadingStock ? (
                  <div className="text-xs text-slate-400 py-2">Loading stock...</div>
                ) : filteredStock.length === 0 ? (
                  <div className="text-xs text-slate-400 py-2">No stock found for this search</div>
                ) : (
                  <div className="max-h-[180px] overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-slate-500 border-b">
                          <th className="text-center py-1 px-2 w-8">Select</th>
                          <th className="text-left py-1 px-2">Circ</th>
                          <th className="text-left py-1 px-2">Length</th>
                          <th className="text-left py-1 px-2">Bore</th>
                          <th className="text-left py-1 px-2">Degree</th>
                          <th className="text-left py-1 px-2">Key Way</th>
                          <th className="text-right py-1 px-2">Avail</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredStock.map((row, i) => (
                          row.details?.map((d, j) => {
                            const stockKey = `${row.circumference}_${row.length}_${d.bore}_${d.degree}_${d.key_way}`;
                            const isSelected = selectedStockKey === stockKey;
                            const isExactMatch = row.circumference === order?.specifications?.circumference &&
                              row.length === order?.specifications?.length &&
                              d.bore === order?.specifications?.bore;
                            return (
                              <tr key={`${i}-${j}`} className={`border-b border-slate-100 ${isSelected ? 'bg-blue-50 ring-1 ring-blue-300' : isExactMatch ? 'bg-green-50' : ''}`}>
                                <td className="py-1 px-2 text-center">
                                  <Checkbox
                                    checked={isSelected}
                                    onCheckedChange={(checked) => setSelectedStockKey(checked ? stockKey : null)}
                                  />
                                </td>
                                <td className="py-1 px-2">{row.circumference}</td>
                                <td className="py-1 px-2">{row.length}</td>
                                <td className="py-1 px-2">{d.bore || '-'}</td>
                                <td className="py-1 px-2">{d.degree || '-'}</td>
                                <td className="py-1 px-2">{d.key_way || '-'}</td>
                                <td className="py-1 px-2 text-right font-semibold">{d.quantity}</td>
                              </tr>
                            );
                          })
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Individual completions — editable per-row */}
          {stageData?.completions && stageData.completions.length > 0 && (
            <div className="p-3 bg-slate-50 rounded-lg">
              <h4 className="font-medium text-slate-900 mb-2 text-sm">Completed by Operators</h4>
              <div className="space-y-1">
                {stageData.completions.map((c, idx) => {
                  const isEditing = editingIdx === idx;
                  const isQuick = c.operator_id === 'quick';
                  const dateStr = c.completed_at ? new Date(c.completed_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '';
                  return (
                    <div key={idx} className={`flex items-center gap-2 text-sm bg-white p-2 rounded border ${isQuick ? 'border-amber-200' : 'border-slate-200'}`} data-testid={`completion-row-${idx}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-900 truncate">
                          {c.operator_name || 'Unknown'}
                          {isQuick && <span className="ml-1 text-[10px] text-amber-700">(quick)</span>}
                        </div>
                        <div className="text-[10px] text-slate-500">{dateStr}</div>
                      </div>
                      {isEditing ? (
                        <>
                          <Input
                            type="number"
                            min="0"
                            value={editQty}
                            onChange={(e) => setEditQty(e.target.value)}
                            className="w-20 h-8 text-center text-sm"
                            data-testid={`completion-edit-input-${idx}`}
                          />
                          <Button size="sm" variant="ghost" className="h-8 px-2 text-emerald-600" onClick={() => saveCompletionEdit(idx)} disabled={editLoading} data-testid={`completion-save-${idx}`}>
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-8 px-2 text-slate-500" onClick={() => { setEditingIdx(null); setEditQty(''); }} data-testid={`completion-cancel-${idx}`}>
                            ✕
                          </Button>
                        </>
                      ) : (
                        <>
                          <span className="text-slate-700 font-semibold tabular-nums whitespace-nowrap">{c.quantity || 0} nos</span>
                          <Button size="sm" variant="ghost" className="h-8 px-2 text-blue-600" onClick={() => { setEditingIdx(idx); setEditQty(String(c.quantity || 0)); }} title="Edit qty" data-testid={`completion-edit-${idx}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-8 px-2 text-red-500 hover:bg-red-50" onClick={() => deleteCompletion(idx, c.operator_name)} disabled={editLoading} title="Remove" data-testid={`completion-delete-${idx}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Input Section */}
          {/* Remove marker — show whenever any completions exist */}
          {stageData?.completions && stageData.completions.length > 0 && (
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                className="border-red-300 text-red-700 hover:bg-red-50"
                onClick={async () => {
                  if (!window.confirm('Reset this stage? This will clear ALL completion entries and revert the stage to pending.')) return;
                  try {
                    await apiClient.delete(`/stages/${stageData.id}/completions`);
                    toast.success(`${stageName}: reset to pending`);
                    onSuccess && onSuccess();
                    onClose();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Failed to reset stage');
                  }
                }}
                data-testid="remove-marker-btn"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1" /> Reset Stage (Remove All Completions)
              </Button>
            </div>
          )}

          {isCompleted ? (
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-center">
              <CheckCircle className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
              <p className="font-semibold text-emerald-900">Stage Complete!</p>
              <p className="text-sm text-emerald-700">All {totalRequired} cylinder(s) completed.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <Label className="text-sm font-medium text-amber-900 whitespace-nowrap">Completion Date:</Label>
                <Input
                  data-testid="modal-completion-date"
                  type="date"
                  value={completionDate}
                  onChange={(e) => setCompletionDate(e.target.value)}
                  className="h-9 w-44 text-sm"
                />
                {completionDate !== new Date().toISOString().slice(0, 10) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCompletionDate(new Date().toISOString().slice(0, 10))}
                    className="h-8 px-2 text-xs text-amber-700"
                  >
                    Today
                  </Button>
                )}
                <span className="text-xs text-amber-700 ml-auto">Used as the progress/completion date for reports</span>
              </div>
              {isNoOperatorStage ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <Label className="text-sm font-medium text-blue-800 whitespace-nowrap">Cylinders to mark:</Label>
                <Input
                  data-testid="team-qty-input"
                  type="number" min="1" max={remaining}
                  value={teamQuantity}
                  onChange={(e) => setTeamQuantity(e.target.value)}
                  placeholder="0" className="w-24 h-9 text-center"
                />
                <span className="text-xs text-blue-600">/ {remaining} remaining</span>
              </div>
              <Button data-testid="submit-completion-button" onClick={handleSubmitNoOperator} disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 h-10">
                {loading ? 'Saving...' : 'Mark Complete'}
              </Button>
            </div>
          ) : fetchingOperators ? (
            <div className="text-center py-4 text-slate-600">Loading operators...</div>
          ) : operators.length === 0 ? (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-900">No operators assigned</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Add employees with "{stageName}" in their "Processes Performed" under Admin → Employee Details.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <Label className="text-sm font-medium text-slate-700">
                Select operators and enter quantity completed as a team:
              </Label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {operators.map((operator) => (
                  <div key={operator.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <Checkbox
                      checked={selectedOperators.includes(operator.id)}
                      onCheckedChange={(checked) => handleOperatorToggle(operator.id, checked)}
                    />
                    <div className="flex items-center gap-2 flex-1">
                      <User className="h-4 w-4 text-slate-500" />
                      <span className="font-medium text-slate-900">{operator.name}</span>
                      {operator.mobile && (<span className="text-xs text-slate-500">({operator.mobile})</span>)}
                    </div>
                  </div>
                ))}
              </div>
              {selectedOperators.length > 0 && (
                <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <Label className="text-sm font-medium text-blue-800 whitespace-nowrap">
                    Cylinders completed ({selectedOperators.length} operator{selectedOperators.length > 1 ? 's' : ''}):
                  </Label>
                  <Input
                    data-testid="team-qty-input"
                    type="number" min="1" max={remaining}
                    value={teamQuantity}
                    onChange={(e) => setTeamQuantity(e.target.value)}
                    placeholder="0" className="w-24 h-9 text-center"
                  />
                  <span className="text-xs text-blue-600">/ {remaining} remaining</span>
                </div>
              )}
              <div>
                <Label className="text-sm">Notes (Optional)</Label>
                <Textarea data-testid="stage-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any remarks..." className="min-h-[60px] mt-2" />
              </div>
              <Button data-testid="submit-completion-button" onClick={handleSubmit} disabled={loading || selectedOperators.length === 0} className="w-full bg-blue-600 hover:bg-blue-700 h-10">
                {loading ? 'Saving...' : 'Save Completion'}
              </Button>
            </div>
          )}
            </>
          )}

          <Button variant="outline" onClick={handleClose} className="w-full h-10">Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StageCompletionModal;
